import { useMemo, useState } from 'react';
import {
  useGetCertificatesAchievementsSummaryQuery,
  downloadDashboardExport,
  DASHBOARD_EXPORT_PATHS,
  type CertificatesAchievementsSummaryParams,
} from '@/entities/Dashboard';
import { Svg } from '@/shared/ui/Svg';
import TagIcon from '@/shared/assets/icons/dashboard/Tag.svg';
import { useToast } from '@/shared/lib/hooks/useToast';
import { buildMetric } from '../../../lib/metrics';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { DashboardFilters, type DashboardFilterValue } from '../../components/DashboardFilters';
import styles from '../../dashboard/Dashboard.module.scss';

const TOP_BADGES: { name: string; courses: number; recipients: number }[] = [];

const TOP_ACHIEVEMENT_STUDENTS: { name: string; subtitle: string; badges: number }[] = [];

interface CertificatesTabProps {
  onExport?: () => void;
}

export const CertificatesTab = ({ onExport: _onExport }: CertificatesTabProps) => {
  const { toast } = useToast();
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [filters, setFilters] = useState<DashboardFilterValue>({});
  const [isExporting, setIsExporting] = useState(false);
  const date = toDashboardDateParam(datePeriod);

  const summaryParams = useMemo<CertificatesAchievementsSummaryParams>(() => {
    const params: CertificatesAchievementsSummaryParams = { date };
    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.courseId && filters.intakeId) params.intakeId = filters.intakeId;
    if (filters.courseId && filters.intakeId && filters.groupId) params.groupId = filters.groupId;
    return params;
  }, [date, filters.courseId, filters.intakeId, filters.groupId]);

  const { data: summary } = useGetCertificatesAchievementsSummaryQuery(summaryParams);

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      { id: '1', title: 'Total Badges Earned', value: '—', change: 0, higherIsBetter: true },
      buildMetric('2', 'Total Certificates Issued', s?.certificatesIssued, 'count'),
      { id: '3', title: 'Badges per Student', value: '—', change: 0, higherIsBetter: true },
    ];
  }, [summary]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadDashboardExport(
        DASHBOARD_EXPORT_PATHS.certificatesAchievements,
        {
          format: 'excel',
          sections: 'summary',
          ...summaryParams,
        },
        'certificates-achievements.xlsx',
      );
      toast({ title: 'Success', description: 'Export completed', color: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to export analytics',
        color: 'danger',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Certificates & Achievements"
          icon={<Svg Icon={TagIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          onExport={isExporting ? undefined : handleExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <DashboardFilters value={filters} onChange={setFilters} showTerm twoRow />
        <AnalyticsMetricsGrid metrics={metrics} columns={3} />
      </section>

      <div className={styles.twoColumns}>
        <section className={styles.section}>
          <AnalyticsSectionHeader
            title="Top 5 Most Earned Badges"
            icon={<Svg Icon={TagIcon} width={20} height={20} className="text-[#0068D6]" />}
            iconBg="#E8F0FF"
            showToolbar={false}
          />
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className="sr-only">Top 5 most earned badges</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Courses</th>
                  <th scope="col">Recipients</th>
                </tr>
              </thead>
              <tbody>
                {TOP_BADGES.map((row) => (
                  <tr key={`${row.name}-${row.recipients}`}>
                    <td>{row.name}</td>
                    <td>{row.courses}</td>
                    <td>{row.recipients}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.section}>
          <AnalyticsSectionHeader
            title="Top Students by Achievements"
            icon={<Svg Icon={TagIcon} width={20} height={20} className="text-[#5B3FD1]" />}
            iconBg="#F0EBFF"
            showToolbar={false}
          />
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className="sr-only">Top students by achievements</caption>
              <thead>
                <tr>
                  <th scope="col">Students</th>
                  <th scope="col">Badges Earned</th>
                </tr>
              </thead>
              <tbody>
                {TOP_ACHIEVEMENT_STUDENTS.map((row) => (
                  <tr key={row.name}>
                    <td>
                      <div className={styles.tableStudentCell}>
                        <span className={styles.tableStudentName}>{row.name}</span>
                        <span className={styles.tableStudentSub}>{row.subtitle}</span>
                      </div>
                    </td>
                    <td>{row.badges}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};
